/**
 * Shared label generation utilities for reporting tables
 *
 * Provides consistent label generation across table components to ensure
 * field names are displayed uniformly with proper context and formatting.
 */

/**
 * Auto-generates human-readable labels from field names.
 * Handles both camelCase and snake_case input, converts to Title Case.
 *
 * @example
 * autoGenerateLabel('employeeName') // 'Employee Name'
 * autoGenerateLabel('employee_name') // 'Employee Name'
 * autoGenerateLabel('id') // 'Id'
 */
export const autoGenerateLabel = (fieldName: string) => {
  // Handle camelCase: insert spaces before capital letters
  let spaced = fieldName.replace(/([A-Z])/g, ' $1').trim();

  // Handle snake_case and kebab-case: replace separators with spaces
  spaced = spaced.replace(/[_-]/g, ' ');

  // Capitalize first letter of each word
  return spaced
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Generates smart labels for columns that keep necessary context
 * to disambiguate similar fields (e.g., "Program: Name" vs "Application: Name")
 *
 * Removes TAS metadata suffixes and prefixes, then adds back context for
 * ambiguous field names (id, state, name, etc.)
 *
 * @example
 * generateSmartLabel('tasProgramInstanceFieldNameKeyword')
 * // Returns: 'Program: Name' (stripped prefix and Keyword suffix, added context)
 *
 * generateSmartLabel('employeeNameKeyword')
 * // Returns: 'Employee Name' (stripped Keyword suffix, detected ambiguous 'Name', added context)
 *
 * generateSmartLabel('tasApplicationFieldCoursesBeginDateDate')
 * // Returns: 'Courses Begin Date' (stripped prefix and type suffixes, not ambiguous)
 */
export const generateSmartLabel = (columnKey: string) => {
  // First, strip suffixes that are just metadata (camelCase only now)
  // Only strip the LAST occurrence (type suffix) - e.g., coursesBeginDateDate → coursesBeginDate
  const cleanKey = columnKey
    .replace(/Keyword$/i, '')
    .replace(/Date$/i, '')
    .replace(/Text$/i, '')
    .replace(/Boolean$/i, '')
    .replace(/Numeric$/i, '')
    .replace(/Integer$/i, '')
    .replace(/Long$/i, '');

  // Special case mappings for fields that need custom labels
  // These are checked early because they override automatic label generation
  const specialMappings: Record<string, string> = {
    paymentLast: 'Date',
    paymentFirst: 'Date',
  };

  if (specialMappings[cleanKey]) {
    return specialMappings[cleanKey];
  }

  // Strip all TAS prefixes and "Field" indicators to get the actual field name
  // CamelCase only since we normalize at entry point
  const baseName = cleanKey
    .replace(/^tasProgramTemplateField/i, '')
    .replace(/^tasProgramInstanceField/i, '')
    .replace(/^tasProgramInstance/i, '')
    .replace(/^tasProgramTemplate/i, '')
    .replace(/^tasApplicationField/i, '')
    .replace(/^tasApplication/i, '')
    .replace(/^tasCourseField/i, '')
    .replace(/^tasCourse/i, '')
    .replace(/^tas/i, '');

  // Check if this is a payment field (before stripping payment prefix)
  const isPaymentField = cleanKey.toLowerCase().startsWith('payment');

  // For payment fields, always use "Payment: " prefix for consistency
  if (isPaymentField) {
    const paymentBaseName = baseName.replace(/^payment/i, '');
    return 'Payment: ' + autoGenerateLabel(paymentBaseName);
  }

  // For ambiguous fields (common field names that need context), keep model prefix
  const ambiguousFields = [
    'id',
    'state',
    'name',
    'createdat',
    'created',
    'updatedat',
    'updated',
    'status',
    'type',
  ];

  const isAmbiguous = ambiguousFields.some((field) => baseName.toLowerCase() === field);

  // Generate label with context for ambiguous fields
  // Check original cleanKey for context (before prefix stripping)
  if (isAmbiguous) {
    const lowerKey = cleanKey.toLowerCase();
    if (lowerKey.includes('programinstance')) {
      return 'Program: ' + autoGenerateLabel(baseName);
    } else if (lowerKey.includes('programtemplate') || lowerKey.includes('program')) {
      return 'Program: ' + autoGenerateLabel(baseName);
    } else if (lowerKey.includes('application')) {
      return 'Application: ' + autoGenerateLabel(baseName);
    } else if (lowerKey.includes('course')) {
      return 'Course: ' + autoGenerateLabel(baseName);
    } else if (lowerKey.includes('payment')) {
      return 'Payment: ' + autoGenerateLabel(baseName);
    } else if (lowerKey.includes('employee')) {
      return 'Employee: ' + autoGenerateLabel(baseName);
    }
  }

  // For non-ambiguous fields, use clean label without any prefix
  return autoGenerateLabel(baseName);
};
